import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import {
    ArrowUp,
    ArrowsFullscreen,
    ChatDotsFill,
    ChatSquareDotsFill,
    Collection,
    FullscreenExit,
    PlusLg,
    Trash3,
    XLg
} from 'react-bootstrap-icons';

import {
    createChatSession,
    deleteChatSession,
    fetchChatHistory,
    listChatSessions,
    streamChatMessage
} from '../../api/chat';
import { ChatHistoryMessage, ChatSessionContext, ChatSessionListItem } from '../../types/chat';
import { formatLocalDateTimeFromUTC } from '../../utils/datetime';
import './ProjectChatPanel.css';

interface ProjectChatPanelProps
{
    projectId: number;
    variant?: 'floating' | 'embedded';
    context?: ChatSessionContext | null;
    title?: string;
    subtitle?: string;
    allowMaximize?: boolean;
}

type ChatWidgetView = 'chat' | 'sessions';

const STREAM_REVEAL_INTERVAL_MS = 18;
const STREAM_REVEAL_MIN_CHARS = 4;
const CHAT_INTERNAL_ERROR_MARKERS = [
    'sqlalchemy',
    'asyncpg',
    'psycopg',
    'interfaceerror',
    'operationalerror',
    'select ',
    'varchar',
    'connection is closed',
    'internal server error',
    'background on this error',
    'traceback',
    'stack trace',
];

interface PanelMessage
{
    type: 'success' | 'danger' | 'info' | 'warning';
    text: string;
}

const CHAT_CREDIT_ACCESS_MESSAGE = 'No free tokens or active subscription is available. Subscribe to a plan or recharge LLM Usage Credits to continue chatting.';

function isChatCreditAccessError(message: string): boolean
{
    const normalized = message.toLowerCase();
    return normalized.includes('free or beta token quota has been exhausted')
        || normalized.includes('subscribe to a plan or recharge llm usage credits');
}

const INITIAL_ASSISTANT_COPY = '👋 Hi! I\'m your AcceliQA assistant. Ask me anything about your projects, test cases, or repositories.';

function buildInitialAssistantMessage(): ChatHistoryMessage
{
    return {
        role: 'assistant',
        content: INITIAL_ASSISTANT_COPY,
        timestamp: null,
    };
}

function buildSessionNameFromPrompt(prompt: string): string
{
    const trimmed = prompt.trim();

    if (!trimmed)
    {
        return 'New Chat';
    }

    if (trimmed.length <= 36)
    {
        return trimmed;
    }

    return `${trimmed.slice(0, 33)}...`;
}

function getSessionTitle(session: ChatSessionListItem): string
{
    const trimmed = (session.session_name || '').trim();

    if (!trimmed)
    {
        return 'New Chat';
    }

    return trimmed;
}

function getSessionSubtitle(context?: ChatSessionContext | null, subtitle?: string): string
{
    if (subtitle)
    {
        return subtitle;
    }

    if (context?.run_id)
    {
        return 'Run-scoped troubleshooting chat.';
    }

    if (context?.test_key)
    {
        return 'Test-scoped troubleshooting chat.';
    }

    return 'Ask about project issues, repositories, requirements, or test coverage.';
}

function sortSessionsByCreatedAt(items: ChatSessionListItem[]): ChatSessionListItem[]
{
    return [...items].sort(
        function compareSessions(a: ChatSessionListItem, b: ChatSessionListItem): number
        {
            return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        }
    );
}

function getSafeChatErrorMessage(error: any, fallback: string): string
{
    const rawDetail = error?.response?.data?.detail;
    const candidate = (
        typeof rawDetail === 'string' && rawDetail.trim()
            ? rawDetail
            : typeof error?.message === 'string' && error.message.trim()
                ? error.message
                : fallback
    );
    const lowered = candidate.toLowerCase();
    if (CHAT_INTERNAL_ERROR_MARKERS.some((marker) => lowered.includes(marker)))
    {
        return fallback;
    }
    return candidate;
}

function buildOptimisticUserMessage(content: string): ChatHistoryMessage
{
    return {
        role: 'user',
        content,
        timestamp: new Date().toISOString(),
    };
}

function buildAssistantPlaceholder(timestamp: string): ChatHistoryMessage
{
    return {
        role: 'assistant',
        content: '',
        timestamp,
    };
}

function renderInlineMarkdown(text: string, keyPrefix: string): React.ReactNode[]
{
    const nodes: React.ReactNode[] = [];
    const pattern = /(\*\*[^*]+\*\*|`[^`]+`)/g;
    let lastIndex = 0;
    let matchIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = pattern.exec(text)) !== null)
    {
        if (match.index > lastIndex)
        {
            nodes.push(text.slice(lastIndex, match.index));
        }

        const token = match[0];
        if (token.startsWith('**') && token.endsWith('**'))
        {
            nodes.push(
                <strong className="chat-widget-markdown-strong" key={`${keyPrefix}-strong-${matchIndex}`}>
                    {token.slice(2, -2)}
                </strong>
            );
        }
        else if (token.startsWith('`') && token.endsWith('`'))
        {
            nodes.push(
                <code className="chat-widget-markdown-code" key={`${keyPrefix}-code-${matchIndex}`}>
                    {token.slice(1, -1)}
                </code>
            );
        }

        lastIndex = pattern.lastIndex;
        matchIndex += 1;
    }

    if (lastIndex < text.length)
    {
        nodes.push(text.slice(lastIndex));
    }

    return nodes;
}

function renderAssistantMarkdown(content: string): React.ReactNode
{
    const normalizedContent = content.replace(/\r\n/g, '\n');
    const lines = normalizedContent.split('\n');
    const blocks: React.ReactNode[] = [];
    let paragraphLines: string[] = [];
    let index = 0;

    function flushParagraph(): void
    {
        if (paragraphLines.length === 0)
        {
            return;
        }

        const paragraphText = paragraphLines.join(' ');
        blocks.push(
            <p className="chat-widget-markdown-paragraph" key={`paragraph-${blocks.length}`}>
                {renderInlineMarkdown(paragraphText, `paragraph-${blocks.length}`)}
            </p>
        );
        paragraphLines = [];
    }

    while (index < lines.length)
    {
        const rawLine = lines[index];
        const line = rawLine.trim();

        if (!line)
        {
            flushParagraph();
            index += 1;
            continue;
        }

        const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
        if (headingMatch)
        {
            flushParagraph();
            blocks.push(
                <div className="chat-widget-markdown-heading" key={`heading-${blocks.length}`}>
                    {renderInlineMarkdown(headingMatch[2], `heading-${blocks.length}`)}
                </div>
            );
            index += 1;
            continue;
        }

        const bulletItems: string[] = [];
        while (index < lines.length)
        {
            const bulletMatch = lines[index].trim().match(/^[-*]\s+(.+)$/);
            if (!bulletMatch)
            {
                break;
            }

            bulletItems.push(bulletMatch[1]);
            index += 1;
        }

        if (bulletItems.length > 0)
        {
            flushParagraph();
            blocks.push(
                <ul className="chat-widget-markdown-list" key={`list-${blocks.length}`}>
                    {bulletItems.map((item, itemIndex) => (
                        <li key={`list-item-${itemIndex}`}>
                            {renderInlineMarkdown(item, `list-${blocks.length}-${itemIndex}`)}
                        </li>
                    ))}
                </ul>
            );
            continue;
        }

        const numberedItems: string[] = [];
        while (index < lines.length)
        {
            const numberedMatch = lines[index].trim().match(/^\d+\.\s+(.+)$/);
            if (!numberedMatch)
            {
                break;
            }

            numberedItems.push(numberedMatch[1]);
            index += 1;
        }

        if (numberedItems.length > 0)
        {
            flushParagraph();
            blocks.push(
                <ol className="chat-widget-markdown-list" key={`ordered-list-${blocks.length}`}>
                    {numberedItems.map((item, itemIndex) => (
                        <li key={`ordered-list-item-${itemIndex}`}>
                            {renderInlineMarkdown(item, `ordered-list-${blocks.length}-${itemIndex}`)}
                        </li>
                    ))}
                </ol>
            );
            continue;
        }

        paragraphLines.push(line);
        index += 1;
    }

    flushParagraph();

    return <div className="chat-widget-message-markdown">{blocks}</div>;
}

function ProjectChatPanel(
    {
        projectId,
        variant = 'floating',
        context = null,
        title,
        subtitle,
        allowMaximize = false,
    }: ProjectChatPanelProps
): React.ReactElement
{
    const isEmbedded = variant === 'embedded';
    const canMaximize = !isEmbedded || allowMaximize;
    const sessionContext = useMemo(
        () => (context ? { ...context } : null),
        [context]
    );
    const [isOpen, setIsOpen] = useState<boolean>(isEmbedded);
    const [activeView, setActiveView] = useState<ChatWidgetView>('chat');
    const [sessions, setSessions] = useState<ChatSessionListItem[]>([]);
    const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
    const [messages, setMessages] = useState<ChatHistoryMessage[]>([]);
    const [draftMessage, setDraftMessage] = useState<string>('');
    const [loadingSessions, setLoadingSessions] = useState<boolean>(false);
    const [loadingHistory, setLoadingHistory] = useState<boolean>(false);
    const [sending, setSending] = useState<boolean>(false);
    const [creatingSession, setCreatingSession] = useState<boolean>(false);
    const [showCreateSession, setShowCreateSession] = useState<boolean>(false);
    const [newSessionName, setNewSessionName] = useState<string>('');
    const [deletingSessionId, setDeletingSessionId] = useState<string | null>(null);
    const [showTypingIndicator, setShowTypingIndicator] = useState<boolean>(false);
    const [panelMessage, setPanelMessage] = useState<PanelMessage | null>(null);
    const [chatCreditsUnavailable, setChatCreditsUnavailable] = useState<boolean>(false);
    const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);
    const [isMaximized, setIsMaximized] = useState<boolean>(false);

    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const messagesContainerRef = useRef<HTMLDivElement | null>(null);
    const draftInputRef = useRef<HTMLInputElement | null>(null);
    const userScrolledUpRef = useRef<boolean>(false);
    const typingAnimationTimeoutRef = useRef<number | null>(null);
    const activeTypingMessageRef = useRef<string | null>(null);
    const activeTypingTextRef = useRef<string>('');
    const activeTypingTargetRef = useRef<string>('');
    const activeTypingFinalTimestampRef = useRef<string | null>(null);
    const skipNextHistoryLoadRef = useRef<boolean>(false);
    function clearTypingAnimation(): void
    {
        if (typingAnimationTimeoutRef.current !== null)
        {
            window.clearTimeout(typingAnimationTimeoutRef.current);
            typingAnimationTimeoutRef.current = null;
        }
    }

    function resetTypingAnimationState(): void
    {
        clearTypingAnimation();
        activeTypingMessageRef.current = null;
        activeTypingTextRef.current = '';
        activeTypingTargetRef.current = '';
        activeTypingFinalTimestampRef.current = null;
    }

    function updateAssistantMessage(messageTimestamp: string, content: string, finalTimestamp?: string | null): void
    {
        const hasFinalTimestamp = typeof finalTimestamp === 'string' && finalTimestamp.length > 0;

        setMessages(
            function updateStreamedAssistantMessage(previousMessages: ChatHistoryMessage[]): ChatHistoryMessage[]
            {
                return previousMessages.map(
                    function mapAssistantMessage(message: ChatHistoryMessage): ChatHistoryMessage
                    {
                        if (message.timestamp === messageTimestamp)
                        {
                            return {
                                ...message,
                                content,
                                ...(hasFinalTimestamp ? { timestamp: finalTimestamp! } : {}),
                            };
                        }

                        return message;
                    }
                );
            }
        );
    }

    function scheduleTypingStep(messageTimestamp: string): void
    {
        if (typingAnimationTimeoutRef.current !== null)
        {
            return;
        }

        typingAnimationTimeoutRef.current = window.setTimeout(
            function revealNextCharacters(): void
            {
                typingAnimationTimeoutRef.current = null;

                if (activeTypingMessageRef.current !== messageTimestamp)
                {
                    return;
                }

                const targetText = activeTypingTargetRef.current;
                const currentText = activeTypingTextRef.current;

                if (currentText.length < targetText.length)
                {
                    const remainingCharacters = targetText.length - currentText.length;
                    const charactersToReveal = Math.max(
                        STREAM_REVEAL_MIN_CHARS,
                        Math.ceil(remainingCharacters / 12)
                    );
                    const nextText = targetText.slice(0, currentText.length + charactersToReveal);
                    activeTypingTextRef.current = nextText;
                    updateAssistantMessage(messageTimestamp, nextText);
                    scheduleTypingStep(messageTimestamp);
                    return;
                }

                const finalTimestamp = activeTypingFinalTimestampRef.current;
                if (finalTimestamp)
                {
                    updateAssistantMessage(messageTimestamp, targetText, finalTimestamp);
                    resetTypingAnimationState();
                }
            },
            STREAM_REVEAL_INTERVAL_MS
        );
    }

    function animateAssistantMessage(messageTimestamp: string, targetText: string, finalTimestamp?: string | null): void
    {
        setShowTypingIndicator(false);

        if (activeTypingMessageRef.current !== messageTimestamp)
        {
            activeTypingMessageRef.current = messageTimestamp;
            activeTypingTextRef.current = '';
            activeTypingTargetRef.current = '';
            activeTypingFinalTimestampRef.current = null;
        }

        if (targetText.length < activeTypingTextRef.current.length)
        {
            activeTypingTextRef.current = '';
        }

        activeTypingTargetRef.current = targetText;

        if (typeof finalTimestamp === 'string' && finalTimestamp.length > 0)
        {
            activeTypingFinalTimestampRef.current = finalTimestamp;
        }

        scheduleTypingStep(messageTimestamp);
    }

    const sortedSessions = useMemo(
        function buildSortedSessions(): ChatSessionListItem[]
        {
            return sortSessionsByCreatedAt(sessions);
        },
        [sessions]
    );

    const activeSession = useMemo(
        function findActiveSession(): ChatSessionListItem | null
        {
            return sessions.find(
                function matchSession(session: ChatSessionListItem): boolean
                {
                    return session.session_id === selectedSessionId;
                }
            ) || null;
        },
        [sessions, selectedSessionId]
    );

    const displayedMessages = useMemo(
        function buildDisplayedMessages(): ChatHistoryMessage[]
        {
            if (messages.length > 0)
            {
                return messages;
            }

            return [buildInitialAssistantMessage()];
        },
        [messages]
    );

    useEffect(
        function resetWidgetForProject(): void
        {
            if (typingAnimationTimeoutRef.current !== null)
            {
                window.clearTimeout(typingAnimationTimeoutRef.current);
                typingAnimationTimeoutRef.current = null;
            }
            activeTypingMessageRef.current = null;
            activeTypingTextRef.current = '';
            activeTypingTargetRef.current = '';
            activeTypingFinalTimestampRef.current = null;
            skipNextHistoryLoadRef.current = false;
            setActiveView('chat');
            setSelectedSessionId(null);
            setSessions([]);
            setMessages([]);
            setDraftMessage('');
            setCreatingSession(false);
            setShowCreateSession(false);
            setNewSessionName('');
            setPanelMessage(null);
            setChatCreditsUnavailable(false);
            setShowTypingIndicator(false);
        },
        [context?.artifact_id, context?.run_id, context?.test_case_id, context?.test_key, isEmbedded, projectId]
    );

    useEffect(
        function setPortalRootOnClient(): void
        {
            if (!isEmbedded)
            {
                setPortalRoot(document.body);
            }
        },
        [isEmbedded]
    );

    useEffect(
        function keepEmbeddedPanelOpen(): void
        {
            if (isEmbedded)
            {
                setIsOpen(true);
            }
        },
        [isEmbedded]
    );

    useEffect(
        function cleanupTypingAnimationOnUnmount(): () => void
        {
            return function cleanupTypingAnimation(): void
            {
                if (typingAnimationTimeoutRef.current !== null)
                {
                    window.clearTimeout(typingAnimationTimeoutRef.current);
                    typingAnimationTimeoutRef.current = null;
                }
                activeTypingMessageRef.current = null;
                activeTypingTextRef.current = '';
                activeTypingTargetRef.current = '';
                activeTypingFinalTimestampRef.current = null;
            };
        },
        []
    );

    const handleLoadSessions = useCallback(
        async function handleLoadSessionsCallback(): Promise<void>
        {
            setLoadingSessions(true);
            setPanelMessage(null);

            try
            {
                const data = await listChatSessions(projectId, sessionContext);
                const sortedData = sortSessionsByCreatedAt(data);
                setSessions(sortedData);

                if (!selectedSessionId && sortedData.length > 0)
                {
                    setSelectedSessionId(sortedData[0].session_id);
                }
            }
            catch (error: any)
            {
                setPanelMessage(
                    {
                        type: 'danger',
                        text: getSafeChatErrorMessage(error, 'Failed to load chat sessions.')
                    }
                );
            }
            finally
            {
                setLoadingSessions(false);
            }
        },
        [projectId, selectedSessionId, sessionContext]
    );

    const handleLoadHistory = useCallback(
        async function handleLoadHistoryCallback(sessionId: string): Promise<void>
        {
            setLoadingHistory(true);
            setPanelMessage(null);

            try
            {
                const data = await fetchChatHistory(sessionId);
                setMessages(data.messages || []);
            }
            catch (error: any)
            {
                setPanelMessage(
                    {
                        type: 'danger',
                        text: getSafeChatErrorMessage(error, 'Failed to load chat history.')
                    }
                );
            }
            finally
            {
                setLoadingHistory(false);
            }
        },
        []
    );

    useEffect(
        function loadSessionsWhenOpened(): void
        {
            if (!isOpen)
            {
                return;
            }

            void handleLoadSessions();
        },
        [handleLoadSessions, isOpen]
    );

    useEffect(
        function loadSessionsWhenViewingSessionsTab(): void
        {
            if (!isOpen || activeView !== 'sessions')
            {
                return;
            }

            void handleLoadSessions();
        },
        [activeView, handleLoadSessions, isOpen]
    );

    useEffect(
        function loadHistoryForSelectedSession(): void
        {
            if (!isOpen || !selectedSessionId)
            {
                return;
            }

            if (skipNextHistoryLoadRef.current)
            {
                skipNextHistoryLoadRef.current = false;
                return;
            }

            void handleLoadHistory(selectedSessionId);
        },
        [handleLoadHistory, isOpen, selectedSessionId]
    );

    useEffect(
        function scrollMessagesToBottom(): void
        {
            // Only auto-scroll if the user hasn't scrolled up to read earlier messages
            if (userScrolledUpRef.current)
            {
                return;
            }

            if (messagesEndRef.current)
            {
                messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }
        },
        [displayedMessages, showTypingIndicator, loadingHistory]
    );

    useEffect(
        function keepDraftInputFocused(): void
        {
            if (!isOpen || activeView !== 'chat' || sending)
            {
                return;
            }

            window.requestAnimationFrame(() => {
                draftInputRef.current?.focus();
            });
        },
        [activeView, isOpen, sending, selectedSessionId]
    );

    function handleMessagesScroll(): void
    {
        const container = messagesContainerRef.current;

        if (!container)
        {
            return;
        }

        // Within 80px of the bottom → treat as "at bottom", re-enable auto-scroll
        const isNearBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 80;
        userScrolledUpRef.current = !isNearBottom;
    }

    function handleOpenWidget(): void
    {
        setIsOpen(true);
    }

    function handleCloseWidget(): void
    {
        setIsOpen(false);
        setIsMaximized(false);
    }

    function handleToggleWidget(): void
    {
        if (isOpen)
        {
            handleCloseWidget();
            return;
        }

        handleOpenWidget();
    }

    function handleSwitchToChat(): void
    {
        setActiveView('chat');
    }

    function handleSwitchToSessions(): void
    {
        setActiveView('sessions');
    }

    async function handleCreateSession(sessionName?: string | null): Promise<string | null>
    {
        try
        {
            setCreatingSession(true);
            const session = await createChatSession(projectId, sessionName || null, sessionContext);
            setSessions(
                function appendSession(previousSessions: ChatSessionListItem[]): ChatSessionListItem[]
                {
                    return sortSessionsByCreatedAt([session, ...previousSessions]);
                }
            );
            skipNextHistoryLoadRef.current = true;
            setSelectedSessionId(session.session_id);
            setMessages([]);
            return session.session_id;
        }
        catch (error: any)
        {
            setPanelMessage(
                {
                    type: 'danger',
                    text: getSafeChatErrorMessage(error, 'Failed to start a new chat.')
                }
            );
            return null;
        }
        finally
        {
            setCreatingSession(false);
        }
    }

    function handleNewChatClick(): void
    {
        setActiveView('sessions');
        setShowCreateSession(true);
        setNewSessionName('');
        setPanelMessage(null);
    }

    function handleCancelCreateSession(): void
    {
        setShowCreateSession(false);
        setNewSessionName('');
    }

    function handleNewSessionNameChange(event: React.ChangeEvent<HTMLInputElement>): void
    {
        setNewSessionName(event.target.value);
    }

    async function handleConfirmCreateSession(): Promise<void>
    {
        const sessionName = newSessionName.trim() || null;
        const createdSessionId = await handleCreateSession(sessionName);

        if (createdSessionId)
        {
            setShowCreateSession(false);
            setNewSessionName('');
            setActiveView('chat');
            setIsOpen(true);
        }
    }

    function handleNewSessionNameKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void
    {
        if (event.key === 'Enter')
        {
            event.preventDefault();
            void handleConfirmCreateSession();
            return;
        }

        if (event.key === 'Escape')
        {
            event.preventDefault();
            handleCancelCreateSession();
        }
    }

    function handleToggleMaximized(): void
    {
        if (!canMaximize)
        {
            return;
        }

        setIsMaximized((currentValue) => !currentValue);
    }

    async function handleDeleteSession(sessionId: string): Promise<void>
    {
        setDeletingSessionId(sessionId);

        try
        {
            await deleteChatSession(sessionId, projectId);
            setSessions(
                function removeSession(previousSessions: ChatSessionListItem[]): ChatSessionListItem[]
                {
                    return previousSessions.filter(
                        function filterSession(session: ChatSessionListItem): boolean
                        {
                            return session.session_id !== sessionId;
                        }
                    );
                }
            );

            if (selectedSessionId === sessionId)
            {
                setSelectedSessionId(null);
                setMessages([]);
            }
        }
        catch (error: any)
        {
            setPanelMessage(
                {
                    type: 'danger',
                    text: getSafeChatErrorMessage(error, 'Failed to delete chat session.')
                }
            );
        }
        finally
        {
            setDeletingSessionId(null);
        }
    }

    function handleSelectSession(sessionId: string): void
    {
        setSelectedSessionId(sessionId);
        setActiveView('chat');
    }

    function handleDraftChange(event: React.ChangeEvent<HTMLInputElement>): void
    {
        setDraftMessage(event.target.value);
    }

    function handleDraftKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void
    {
        if (event.key === 'Enter' && !event.shiftKey)
        {
            event.preventDefault();
            void handleSendMessage();
        }
    }

    async function handleSendMessage(): Promise<void>
    {
        const trimmedMessage = draftMessage.trim();

        if (!trimmedMessage || sending || chatCreditsUnavailable)
        {
            return;
        }

        // Disable button immediately so the user sees feedback before session creation
        setSending(true);

        let activeSessionId = selectedSessionId;

        if (!activeSessionId)
        {
            activeSessionId = await handleCreateSession(buildSessionNameFromPrompt(trimmedMessage));
        }

        if (!activeSessionId)
        {
            setSending(false);
            return;
        }

        const optimisticUserMessage = buildOptimisticUserMessage(trimmedMessage);
        const assistantTimestamp = new Date(Date.now() + 1).toISOString();
        const assistantPlaceholder = buildAssistantPlaceholder(assistantTimestamp);

        resetTypingAnimationState();
        activeTypingMessageRef.current = assistantTimestamp;
        setDraftMessage('');
        setShowTypingIndicator(true);
        // Reset scroll position lock — always scroll to show the user's own new message
        userScrolledUpRef.current = false;
        setMessages(
            function appendOutgoingMessage(previousMessages: ChatHistoryMessage[]): ChatHistoryMessage[]
            {
                return [...previousMessages, optimisticUserMessage, assistantPlaceholder];
            }
        );

        try
        {
            await streamChatMessage(
                activeSessionId,
                trimmedMessage,
                function handleStreamEvent(event): void
                {
                    if (event.status === 'streaming')
                    {
                        animateAssistantMessage(assistantTimestamp, event.accumulated ?? '');
                    }

                    if (event.status === 'complete')
                    {
                        animateAssistantMessage(
                            assistantTimestamp,
                            event.full_response ?? '',
                            event.timestamp ?? assistantTimestamp
                        );
                    }

                    if (event.status === 'error')
                    {
                        throw new Error(event.error || 'The assistant could not complete this response.');
                    }
                }
            );
        }
        catch (error: any)
        {
            resetTypingAnimationState();
            setShowTypingIndicator(false);
            const errorMessage = getSafeChatErrorMessage(error, 'Failed to send chat message.');
            const isCreditAccessError = isChatCreditAccessError(errorMessage);
            if (isCreditAccessError)
            {
                setChatCreditsUnavailable(true);
            }
            setPanelMessage(
                {
                    type: isCreditAccessError ? 'warning' : 'danger',
                    text: isCreditAccessError ? CHAT_CREDIT_ACCESS_MESSAGE : errorMessage
                }
            );
            setMessages(
                function applyFailureMessage(previousMessages: ChatHistoryMessage[]): ChatHistoryMessage[]
                {
                    return previousMessages.map(
                        function mapFailureMessage(message: ChatHistoryMessage): ChatHistoryMessage
                        {
                            if (message.timestamp === assistantTimestamp)
                            {
                                return {
                                    ...message,
                                    content: isCreditAccessError ? CHAT_CREDIT_ACCESS_MESSAGE : errorMessage,
                                };
                            }

                            return message;
                        }
                    );
                }
            );
        }
        finally
        {
            setSending(false);
            void handleLoadSessions();
        }
    }

    function renderHeader(): React.ReactNode
    {
        return (
            <div className={`chat-widget-header ${isEmbedded ? 'chat-widget-header-embedded' : ''}`}>
                <div className="chat-widget-header-left">
                    <div className="chat-widget-brand-icon">
                        <ChatSquareDotsFill size={18} />
                    </div>
                    <div>
                        <div className="chat-widget-title">{title || 'AcceliQA AI Assistant'}</div>
                        <div className="chat-widget-subtitle">{getSessionSubtitle(context, subtitle)}</div>
                    </div>
                </div>

                <div className="chat-widget-header-actions">
                    {canMaximize && (
                        <button
                            className="chat-widget-header-icon-btn"
                            type="button"
                            onClick={handleToggleMaximized}
                            aria-label={isMaximized ? 'Restore chat' : 'Maximize chat'}
                            title={isMaximized ? 'Restore chat' : 'Maximize chat'}
                        >
                            {isMaximized ? <FullscreenExit size={17} /> : <ArrowsFullscreen size={17} />}
                        </button>
                    )}

                    {!isEmbedded && (
                        <button className="chat-widget-header-icon-btn" type="button" onClick={handleCloseWidget} aria-label="Close assistant" title="Close assistant">
                            <XLg size={18} />
                        </button>
                    )}
                </div>
            </div>
        );
    }

    function renderTabs(): React.ReactNode
    {
        return (
            <div className="chat-widget-tabs">
                <button
                    className={`chat-widget-tab ${activeView === 'chat' ? 'chat-widget-tab-active' : ''}`}
                    type="button"
                    onClick={handleSwitchToChat}
                >
                    <ChatDotsFill size={14} />
                    Chat
                </button>

                <button
                    className={`chat-widget-tab ${activeView === 'sessions' ? 'chat-widget-tab-active' : ''}`}
                    type="button"
                    onClick={handleSwitchToSessions}
                >
                    <Collection size={14} />
                    Sessions
                </button>

                <button className="chat-widget-new-btn" type="button" onClick={handleNewChatClick} disabled={creatingSession}>
                    <PlusLg size={14} />
                    New
                </button>
            </div>
        );
    }

    function renderPanelMessage(): React.ReactNode
    {
        if (!panelMessage)
        {
            return null;
        }

        return (
            <Alert
                variant={panelMessage.type}
                className="chat-widget-alert"
                dismissible={!chatCreditsUnavailable}
                onClose={() => setPanelMessage(null)}
            >
                {panelMessage.text}
            </Alert>
        );
    }

    function renderSessionStrip(): React.ReactNode
    {
        if (!activeSession)
        {
            return null;
        }

        return (
            <div className="chat-widget-session-strip">
                <span className="chat-widget-session-strip-name">{getSessionTitle(activeSession)}</span>
                <button className="chat-widget-session-strip-link" type="button" onClick={handleSwitchToSessions}>
                    All sessions →
                </button>
            </div>
        );
    }

    function renderTypingBubble(): React.ReactNode
    {
        if (!showTypingIndicator)
        {
            return null;
        }

        return (
            <div className="chat-widget-message-row chat-widget-message-row-assistant">
                <div className="chat-widget-message-bubble chat-widget-message-bubble-assistant chat-widget-typing-bubble">
                    <span className="chat-widget-typing-dot" />
                    <span className="chat-widget-typing-dot" />
                    <span className="chat-widget-typing-dot" />
                </div>
            </div>
        );
    }

    function renderChatMessages(): React.ReactNode
    {
        if (loadingHistory)
        {
            return (
                <div className="chat-widget-loading-state">
                    <Spinner animation="border" size="sm" />
                </div>
            );
        }

        return (
            <div
                className="chat-widget-messages-scroll"
                ref={messagesContainerRef}
                onScroll={handleMessagesScroll}
            >
                {displayedMessages.map(
                    function renderMessage(message: ChatHistoryMessage, index: number): React.ReactNode
                    {
                        const isUserMessage = message.role === 'user';
                        const rowClassName = isUserMessage
                            ? 'chat-widget-message-row chat-widget-message-row-user'
                            : 'chat-widget-message-row chat-widget-message-row-assistant';
                        const bubbleClassName = isUserMessage
                            ? 'chat-widget-message-bubble chat-widget-message-bubble-user'
                            : 'chat-widget-message-bubble chat-widget-message-bubble-assistant';

                        return (
                            <div className={rowClassName} key={`${message.role}-${index}-${message.timestamp || 'starter'}`}>
                                <div className={bubbleClassName}>
                                    <div className="chat-widget-message-text">
                                        {isUserMessage ? message.content : renderAssistantMarkdown(message.content)}
                                    </div>
                                </div>
                                {message.timestamp && (
                                    <div className="chat-widget-message-time">{formatLocalDateTimeFromUTC(message.timestamp)}</div>
                                )}
                            </div>
                        );
                    }
                )}

                {renderTypingBubble()}
                <div ref={messagesEndRef} />
            </div>
        );
    }

    function renderSessionsList(): React.ReactNode
    {
        if (loadingSessions)
        {
            return (
                <div className="chat-widget-loading-state">
                    <Spinner animation="border" size="sm" />
                </div>
            );
        }

        return (
            <div className="chat-widget-sessions-view">
                <div className="chat-widget-sessions-count">{sortedSessions.length} sessions</div>

                {showCreateSession && (
                    <div className="chat-widget-session-create">
                        <input
                            className="chat-widget-session-create-input"
                            type="text"
                            value={newSessionName}
                            placeholder="Session name (optional)"
                            onChange={handleNewSessionNameChange}
                            onKeyDown={handleNewSessionNameKeyDown}
                            disabled={creatingSession}
                        />

                        <div className="chat-widget-session-create-actions">
                            <button
                                className="chat-widget-session-create-btn chat-widget-session-create-btn-secondary"
                                type="button"
                                onClick={handleCancelCreateSession}
                                disabled={creatingSession}
                            >
                                Cancel
                            </button>

                            <button
                                className="chat-widget-session-create-btn chat-widget-session-create-btn-primary"
                                type="button"
                                onClick={() => void handleConfirmCreateSession()}
                                disabled={creatingSession}
                            >
                                {creatingSession ? 'Creating...' : 'Create'}
                            </button>
                        </div>
                    </div>
                )}

                <div className="chat-widget-sessions-list">
                    {sortedSessions.map(
                        function renderSessionCard(session: ChatSessionListItem): React.ReactNode
                        {
                            const isActive = session.session_id === selectedSessionId;
                            const cardClassName = isActive
                                ? 'chat-widget-session-card chat-widget-session-card-active'
                                : 'chat-widget-session-card';

                            return (
                                <div className={cardClassName} key={session.session_id}>
                                    <button
                                        className="chat-widget-session-card-main"
                                        type="button"
                                        onClick={() => handleSelectSession(session.session_id)}
                                    >
                                        <div className="chat-widget-session-card-title">{getSessionTitle(session)}</div>
                                        <div className="chat-widget-session-card-subtitle">{getSessionSubtitle(context, subtitle)}</div>
                                        <div className="chat-widget-session-card-time">
                                            {formatLocalDateTimeFromUTC(session.created_at)}
                                        </div>
                                    </button>

                                    <button
                                        className="chat-widget-session-delete-btn"
                                        type="button"
                                        onClick={() => void handleDeleteSession(session.session_id)}
                                        disabled={deletingSessionId === session.session_id}
                                        aria-label={`Delete ${getSessionTitle(session)}`}
                                        title={`Delete ${getSessionTitle(session)}`}
                                    >
                                        <Trash3 size={16} />
                                    </button>
                                </div>
                            );
                        }
                    )}
                </div>
            </div>
        );
    }

    function renderBody(): React.ReactNode
    {
        return (
            <div className="chat-widget-body">
                {renderPanelMessage()}
                {activeView === 'chat' ? renderSessionStrip() : null}
                {activeView === 'chat' ? renderChatMessages() : renderSessionsList()}
            </div>
        );
    }

    function renderFooter(): React.ReactNode
    {
        const sendDisabled = sending || chatCreditsUnavailable || !draftMessage.trim();

        return (
            <div className="chat-widget-footer">
                <input
                    ref={draftInputRef}
                    className="chat-widget-input"
                    type="text"
                    placeholder={chatCreditsUnavailable ? 'Chat is unavailable.' : 'Ask anything...'}
                    value={draftMessage}
                    onChange={handleDraftChange}
                    onKeyDown={handleDraftKeyDown}
                    disabled={sending || chatCreditsUnavailable}
                />

                <button
                    className={`chat-widget-send-btn ${sendDisabled ? 'chat-widget-send-btn-disabled' : ''}`}
                    type="button"
                    onClick={() => void handleSendMessage()}
                    disabled={sendDisabled}
                    aria-label="Send message"
                    title="Send message"
                >
                    <ArrowUp size={20} />
                </button>
            </div>
        );
    }

    function renderLauncher(): React.ReactNode
    {
        if (isEmbedded)
        {
            return null;
        }

        return (
            <button className="chat-widget-launcher" type="button" onClick={handleToggleWidget} aria-label={isOpen ? 'Close assistant' : 'Open assistant'} title={isOpen ? 'Close assistant' : 'Open assistant'}>
                {isOpen ? <XLg size={26} /> : <ChatDotsFill size={24} />}
            </button>
        );
    }

    const panelClassName = [
        'chat-widget-panel',
        isEmbedded ? 'chat-widget-panel-embedded' : '',
        isMaximized ? 'chat-widget-panel-maximized' : '',
    ].filter(Boolean).join(' ');

    const widgetContent = (
        <>
            {(isEmbedded || isOpen) && isMaximized && (
                <button
                    className="chat-widget-maximize-backdrop"
                    type="button"
                    onClick={handleToggleMaximized}
                    aria-label="Restore chat"
                />
            )}

            {(isEmbedded || isOpen) && (
                <div className={panelClassName}>
                    {renderHeader()}
                    {renderTabs()}
                    {renderBody()}
                    {renderFooter()}
                </div>
            )}

            {renderLauncher()}
        </>
    );

    if (portalRoot && !isEmbedded)
    {
        return createPortal(widgetContent, portalRoot);
    }

    return widgetContent;
}

export default ProjectChatPanel;
